import React, { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DsButton from '../../common/DsButton';
import ExplanationButton from '../../common/ExplanationButton';
import DsModal from '../../common/DsModal';
import RawDsSelect from '../../common/DsSelect';
import {
  formatCurrencyJPY,
  formatDateJP,
  formatDateTimeJST,
  formatItemCount,
  formatNumberJP,
} from '../../../utils/datetime';

import type { EventMarketing, EventMarketingInitiative, EventMarketingLog } from '../../../api/eventMarketing';
import EventMarketingSalesCorrelationChart, { type TicketSalesMarketingTimeline } from './EventMarketingSalesCorrelationChart';
import '../../../css/admin/events/EventMarketingBoard.css';

const initiativeSchedule = (item: EventMarketingInitiative) => item.scheduleKind === 'single'
  ? { label: '実施日時', value: item.scheduledAt ? formatDateTimeJST(item.scheduledAt) : '未設定' }
  : { label: '期間', value: [formatDateJP(item.startOn), formatDateJP(item.endOn)].filter(Boolean).join(' 〜 ') || '未設定' };

export const INITIATIVE_STATUS_LABELS = {
  planned: '予定',
  active: '実施中',
  done: '完了',
  cancelled: '中止',
};

type AdminCandidate = { id?: number; admin_user_id?: number; display_name?: string; full_name?: string; nickname?: string; username?: string };
type SelectProps = { value?: string; onChange: (event: { target: { value: string } }) => void; children: ReactNode };
const DsSelect = RawDsSelect as unknown as React.ComponentType<SelectProps>;
type MarketingBoardView = {
  marketing: EventMarketing;
  adminCandidates: AdminCandidate[];
  canEdit: boolean;
  canViewTasks: boolean;
  canViewTicketSales: boolean;
  ticketSalesLoading: boolean;
  ticketSalesTimeline: TicketSalesMarketingTimeline | null;
  openEditor: (kind: 'initiative' | 'log' | 'metric', index?: number | null, defaults?: Record<string, unknown>) => void;
  reuse: { openTask: (initiative: EventMarketingInitiative) => void };
};

const numericValue = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatYen = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '—';
  return formatCurrencyJPY(value, { prefix: '', suffix: '円' });
};

const compareObservedAt = (left: { occurredAt?: string; observedOn?: string }, right: { occurredAt?: string; observedOn?: string }): number => {
  const leftTime = Date.parse(left?.occurredAt || left?.observedOn || '') || 0;
  const rightTime = Date.parse(right?.occurredAt || right?.observedOn || '') || 0;
  return rightTime - leftTime;
};

export const buildMarketingBoardSummary = (marketing: EventMarketing | null | undefined) => {
  const initiatives = Array.isArray(marketing?.initiatives) ? marketing.initiatives : [];
  const logs = Array.isArray(marketing?.logs) ? marketing.logs : [];
  const metrics = Array.isArray(marketing?.metrics) ? marketing.metrics : [];
  const statusCounts = initiatives.reduce((counts, item) => ({
    ...counts,
    [item.status]: (counts[item.status] || 0) + 1,
  }), {} as Record<string, number>);
  const logCountByInitiative = new Map();
  const metricCountByInitiative = new Map();
  logs.forEach((item) => {
    const id = String(item.initiativeId || '');
    if (id) logCountByInitiative.set(id, (logCountByInitiative.get(id) || 0) + 1);
  });
  metrics.forEach((item) => {
    const id = String(item.initiativeId || '');
    if (id) metricCountByInitiative.set(id, (metricCountByInitiative.get(id) || 0) + 1);
  });
  const reviewInitiativeIds = new Set(initiatives
    .filter((item) => {
      const id = String(item.id || '');
      if (!id) return false;
      if (item.status === 'done') return !metricCountByInitiative.get(id);
      if (item.status === 'active') return !logCountByInitiative.get(id);
      return false;
    })
    .map((item) => String(item.id)));
  const plannedBudget = marketing?.plannedBudget ?? initiatives.reduce(
    (total, item) => total + numericValue(item.plannedBudget),
    0,
  );
  const actualBudget = initiatives.reduce(
    (total, item) => total + numericValue(item.actualBudget),
    0,
  );
  const kpiName = String(marketing?.primaryKpi?.name || '').trim();
  const latestKpiMetric = metrics
    .filter((item) => kpiName && String(item.name || '').trim() === kpiName)
    .sort(compareObservedAt)[0] || null;

  return {
    initiatives,
    logs,
    metrics,
    statusCounts,
    logCountByInitiative,
    metricCountByInitiative,
    reviewInitiativeIds,
    plannedBudget: numericValue(plannedBudget),
    actualBudget,
    latestKpiMetric,
  };
};

function MarketingSummaryItem({ label, value, detail, tone = '' }: { label: string; value: string; detail: string; tone?: string }) {
  return (
    <div className={`event-marketing-board-summary__item${tone ? ` is-${tone}` : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function InitiativeStatusBreakdown({ statusCounts }: { statusCounts: Record<string, number> }) {
  return (
    <div className="event-marketing-board-statuses" aria-label="施策の状態内訳">
      {Object.entries(INITIATIVE_STATUS_LABELS).map(([status, label]) => (
        <span key={status}>
          <i className={`event-marketing-board-statuses__dot is-${status}`} aria-hidden="true" />
          {label} <strong>{formatNumberJP(statusCounts[status] || 0)}</strong>
        </span>
      ))}
    </div>
  );
}

type InitiativeAction = { label: string; icon: string; run: () => void };

function InitiativeActionsMenu({ title, actions }: { title: string; actions: InitiativeAction[] }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, right: 0, openAbove: false });
  const triggerRootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLButtonElement | null>(null);
  const menuId = `event-marketing-initiative-actions-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    if (!open) return undefined;
    firstItemRef.current?.focus();
    const closeOnOutsidePointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!triggerRootRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      triggerRootRef.current?.querySelector('button')?.focus();
    };
    const closeOnViewportChange = () => setOpen(false);
    document.addEventListener('mousedown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', closeOnViewportChange);
    window.addEventListener('scroll', closeOnViewportChange, true);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', closeOnViewportChange);
      window.removeEventListener('scroll', closeOnViewportChange, true);
    };
  }, [open]);

  const toggleMenu = () => {
    if (!open && triggerRootRef.current) {
      const rect = triggerRootRef.current.getBoundingClientRect();
      const estimatedHeight = actions.length * 44 + 10;
      const openAbove = window.innerHeight - rect.bottom < estimatedHeight && rect.top > estimatedHeight;
      setPosition({
        top: openAbove ? rect.top - 4 : rect.bottom + 4,
        right: Math.max(8, window.innerWidth - rect.right),
        openAbove,
      });
    }
    setOpen((current) => !current);
  };

  const runAction = (action: () => void) => {
    setOpen(false);
    action();
  };

  if (!actions.length) return null;
  return (
    <>
      <div ref={triggerRootRef} className="event-marketing-board-actions-menu__trigger">
        <DsButton
          variant="secondary"
          size="sm"
          icon
          aria-label={`${title}の操作`}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={menuId}
          onClick={toggleMenu}
        >
          <i className="fa-solid fa-ellipsis-vertical" aria-hidden="true" />
        </DsButton>
      </div>
      {open ? createPortal(
        <div
          ref={menuRef}
          id={menuId}
          className={`event-marketing-board-actions-menu ds-compact-action-menu${position.openAbove ? ' is-above' : ''}`}
          role="menu"
          style={{ top: position.top, right: position.right }}
        >
          {actions.map((action, index) => (
            <button
              key={action.label}
              ref={index === 0 ? firstItemRef : undefined}
              type="button"
              role="menuitem"
              onClick={() => runAction(action.run)}
            >
              <i className={action.icon} aria-hidden="true" />
              <span>{action.label}</span>
            </button>
          ))}
        </div>,
        document.body,
      ) : null}
    </>
  );
}

export default function EventMarketingBoard({ view }: { view: MarketingBoardView }) {
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [detailInitiative, setDetailInitiative] = useState<EventMarketingInitiative | null>(null);
  const summary = useMemo(() => buildMarketingBoardSummary(view.marketing), [view.marketing]);
  const ownerNames = useMemo(() => new Map<number, string>(view.adminCandidates.map((candidate) => [
    Number(candidate.id ?? candidate.admin_user_id),
    candidate.display_name || candidate.full_name || candidate.nickname || candidate.username || '',
  ])), [view.adminCandidates]);
  const latestLogByInitiative = useMemo(() => {
    const map = new Map<string, EventMarketingLog>();
    [...summary.logs].sort(compareObservedAt).forEach((item) => {
      const id = String(item.initiativeId || '');
      if (id && !map.has(id)) map.set(id, item);
    });
    return map;
  }, [summary.logs]);
  const normalizedQuery = searchQuery.trim().toLocaleLowerCase('ja-JP');
  const filteredInitiatives = summary.initiatives.filter((item) => {
    if (statusFilter && item.status !== statusFilter) return false;
    if (!normalizedQuery) return true;
    const ownerName = ownerNames.get(Number(item.ownerAdminId)) || '';
    return [item.title, item.channel, item.message, item.notes, ownerName]
      .some((value) => String(value || '').toLocaleLowerCase('ja-JP').includes(normalizedQuery));
  });
  const budgetRate = summary.plannedBudget > 0
    ? Math.round((summary.actualBudget / summary.plannedBudget) * 100)
    : null;
  const primaryKpi = view.marketing.primaryKpi || {};
  const latestKpiValue = summary.latestKpiMetric?.value;
  const kpiTarget = numericValue(primaryKpi.target);
  const kpiRate = kpiTarget > 0 && latestKpiValue !== null && latestKpiValue !== undefined
    ? Math.max(0, Math.round((numericValue(latestKpiValue) / kpiTarget) * 100))
    : null;
  const detailId = String(detailInitiative?.id || '');
  const detailIndex = detailInitiative ? summary.initiatives.indexOf(detailInitiative) : -1;
  const detailLogCount = summary.logCountByInitiative.get(detailId) || 0;
  const detailMetricCount = summary.metricCountByInitiative.get(detailId) || 0;
  const detailLatestLog = latestLogByInitiative.get(detailId);
  const detailOwnerName = detailInitiative
    ? ownerNames.get(Number(detailInitiative.ownerAdminId)) || '未設定'
    : '未設定';
  const runDetailAction = (action: () => void) => {
    setDetailInitiative(null);
    action();
  };
  const initiativeRows = filteredInitiatives.map((item) => {
    const id = String(item.id || '');
    const initiativeIndex = summary.initiatives.indexOf(item);
    const logCount = summary.logCountByInitiative.get(id) || 0;
    const metricCount = summary.metricCountByInitiative.get(id) || 0;
    const latestLog = latestLogByInitiative.get(id);
    const ownerName = ownerNames.get(Number(item.ownerAdminId)) || '未設定';
    const budgetDelta = numericValue(item.plannedBudget) - numericValue(item.actualBudget);
    const hasBudget = item.plannedBudget !== null && item.plannedBudget !== undefined
      || item.actualBudget !== null && item.actualBudget !== undefined;
    const actions: InitiativeAction[] = [
      ...(view.canEdit ? [
        { label: '実施を記録', icon: 'fa-solid fa-clipboard-check', run: () => view.openEditor('log', null, { initiativeId: item.id }) },
        { label: '効果を追加', icon: 'fa-solid fa-chart-line', run: () => view.openEditor('metric', null, { initiativeId: item.id }) },
      ] : []),
      ...(item.id && view.canViewTasks ? [{ label: 'タスク', icon: 'fa-solid fa-list-check', run: () => view.reuse.openTask(item) }] : []),
      ...(view.canEdit ? [{ label: '編集', icon: 'fa-solid fa-pen', run: () => view.openEditor('initiative', initiativeIndex) }] : []),
    ];
    return { item, id, initiativeIndex, logCount, metricCount, latestLog, ownerName, budgetDelta, hasBudget, actions };
  });

  const openInitiativeFromKeyboard = (event: React.KeyboardEvent, item: EventMarketingInitiative) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setDetailInitiative(item);
  };

  return (
    <section className="event-marketing-board" role="tabpanel" aria-label="マーケティングボード">
      <div className="event-marketing-board-summary">
        <MarketingSummaryItem label="施策" value={formatItemCount(summary.initiatives.length, '0件')} detail={`${formatNumberJP(summary.statusCounts.active || 0)}件を実施中`} />
        <MarketingSummaryItem
          label="予算"
          value={formatYen(summary.actualBudget)}
          detail={`計画 ${formatYen(summary.plannedBudget)}${budgetRate === null ? '' : ` ・ 消化 ${formatNumberJP(budgetRate)}%`}`}
          tone={budgetRate !== null && budgetRate > 100 ? 'attention' : ''}
        />
        <MarketingSummaryItem label="実施記録 / 効果" value={`${formatNumberJP(summary.logs.length)} / ${formatNumberJP(summary.metrics.length)}`} detail="施策に紐づく活動と観測値" />
        <MarketingSummaryItem
          label="要レビュー"
          value={formatItemCount(summary.reviewInitiativeIds.size, '0件')}
          detail="実施中の記録・完了後の効果が未登録"
          tone={summary.reviewInitiativeIds.size ? 'attention' : 'positive'}
        />
        <MarketingSummaryItem
          label={primaryKpi.name || '主要KPI'}
          value={latestKpiValue === null || latestKpiValue === undefined ? '未観測' : `${formatNumberJP(latestKpiValue)}${primaryKpi.unit || summary.latestKpiMetric?.unit || ''}`}
          detail={kpiTarget > 0 ? `目標 ${formatNumberJP(kpiTarget)}${primaryKpi.unit || ''}${kpiRate === null ? '' : ` ・ 達成 ${formatNumberJP(kpiRate)}%`}` : '戦略で目標値を設定できます'}
        />
      </div>

      {view.canViewTicketSales ? (
        <EventMarketingSalesCorrelationChart
          timeline={view.ticketSalesTimeline}
          logs={summary.logs}
          loading={view.ticketSalesLoading}
        />
      ) : null}

      <div className="event-marketing-board__head">
        <div>
          <h2>施策の進行状況</h2>
          <InitiativeStatusBreakdown statusCounts={summary.statusCounts} />
        </div>
        <div className="event-marketing-board__head-actions">
          {view.canEdit ? <DsButton size="sm" onClick={() => view.openEditor('initiative')}>施策を追加</DsButton> : null}
          <ExplanationButton
            title="マーケティングボードの使い方"
            ariaLabel="マーケティングボードの使い方"
            body={null}
          >
            <ol className="event-marketing-board-guide">
              <li><strong>施策を決める</strong><span>「出演者紹介を週2回投稿」など、誰に・何を・どこで届けるかを登録します。</span></li>
              <li><strong>実施を記録する</strong><span>公開日、実施内容、使った費用を残します。施策カードの3点メニューからすぐ追加できます。</span></li>
              <li><strong>効果を測る</strong><span>閲覧数、申込数、販売数などを記録し、施策前後の変化を確認します。</span></li>
              <li><strong>学びを次へつなぐ</strong><span>有効だった訴求や改善点を残すと、次回イベントへのコピーやAI相談で再利用できます。</span></li>
            </ol>
          </ExplanationButton>
        </div>
      </div>

      <div className="event-marketing-board-toolbar">
        <label className="event-marketing-board-search">
          <span className="sr-only">施策を検索</span>
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
          <input type="search" value={searchQuery} placeholder="施策名・チャネル・担当で検索" onChange={(event) => setSearchQuery(event.target.value)} />
        </label>
        <div className="event-marketing-board-filter">
          <DsSelect
            value={statusFilter}
            aria-label="状態で絞り込む"
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="">状態：すべて</option>
            {Object.entries(INITIATIVE_STATUS_LABELS).map(([status, label]) => <option key={status} value={status}>状態：{label}</option>)}
          </DsSelect>
        </div>
        <span className="event-marketing-board-toolbar__count">{formatItemCount(filteredInitiatives.length, '0件')}を表示</span>
      </div>

      {filteredInitiatives.length ? (
        <div className="event-marketing-board-table-wrap">
          <table className="event-marketing-board-table">
            <thead><tr><th scope="col">施策</th><th scope="col">日程・担当</th><th scope="col">予算</th><th scope="col">記録 / 効果</th><th scope="col">最新の実施</th><th scope="col"><span className="sr-only">操作</span></th></tr></thead>
            <tbody>
              {initiativeRows.map(({ item, id, initiativeIndex, logCount, metricCount, latestLog, ownerName, budgetDelta, hasBudget, actions }) => {
                return (
                  <tr
                    key={item.id || `initiative-${initiativeIndex}`}
                    className={summary.reviewInitiativeIds.has(id) ? 'is-review-needed' : ''}
                    data-marketing-initiative-id={item.id || undefined}
                    tabIndex={0}
                    aria-label={`${item.title || '名称未設定'}の詳細を表示`}
                    onClick={() => setDetailInitiative(item)}
                    onKeyDown={(event) => openInitiativeFromKeyboard(event, item)}
                  >
                    <td className="event-marketing-board-table__initiative">
                      <div>
                        <span className={`event-marketing-status is-${item.status}`}>{INITIATIVE_STATUS_LABELS[item.status] || item.status}</span>
                        {item.channel ? <span className="event-marketing-board-table__channel">{item.channel}</span> : null}
                        {summary.reviewInitiativeIds.has(id) ? <span className="event-marketing-board-table__review">要記録</span> : null}
                      </div>
                      <strong>{item.title || '名称未設定'}</strong>
                      <small>{item.message || item.notes || '訴求・メモ未設定'}</small>
                    </td>
                    <td><strong>{initiativeSchedule(item).value}</strong><small>{ownerName}</small></td>
                    <td><strong>{formatYen(item.actualBudget)} / {formatYen(item.plannedBudget)}</strong><small className={budgetDelta < 0 ? 'is-over' : ''}>{hasBudget ? (budgetDelta < 0 ? `${formatYen(Math.abs(budgetDelta))}超過` : `残り ${formatYen(budgetDelta)}`) : '予算未設定'}</small></td>
                    <td><strong>{formatNumberJP(logCount)}件 / {formatNumberJP(metricCount)}件</strong><small>{metricCount ? '効果を観測済み' : '効果未登録'}</small></td>
                    <td className="event-marketing-board-table__latest"><strong>{latestLog?.summary || '実施記録なし'}</strong><small>{latestLog?.occurredAt ? formatDateTimeJST(latestLog.occurredAt) : '—'}</small></td>
                    <td>
                      <div
                        className="event-marketing-board-table__actions"
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                      >
                        <InitiativeActionsMenu title={item.title || '施策'} actions={actions} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <div className="event-marketing-empty"><p>{summary.initiatives.length ? '条件に一致する施策はありません。' : '施策はまだ登録されていません。'}</p></div>}
      {filteredInitiatives.length ? (
        <div className="event-marketing-board-cards" aria-label="施策一覧">
          {initiativeRows.map(({ item, id, initiativeIndex, logCount, metricCount, latestLog, ownerName, actions }) => (
            <article
              key={item.id || `initiative-card-${initiativeIndex}`}
              className={`event-marketing-board-card${summary.reviewInitiativeIds.has(id) ? ' is-review-needed' : ''}`}
              data-marketing-initiative-id={item.id || undefined}
              tabIndex={0}
              aria-label={`${item.title || '名称未設定'}の詳細を表示`}
              onClick={() => setDetailInitiative(item)}
              onKeyDown={(event) => openInitiativeFromKeyboard(event, item)}
            >
              <div className="event-marketing-board-card__top">
                <div className="event-marketing-board-card__badges">
                  <span className={`event-marketing-status is-${item.status}`}>{INITIATIVE_STATUS_LABELS[item.status] || item.status}</span>
                  {item.channel ? <span className="event-marketing-board-table__channel">{item.channel}</span> : null}
                  {summary.reviewInitiativeIds.has(id) ? <span className="event-marketing-board-table__review">要記録</span> : null}
                </div>
                <div onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                  <InitiativeActionsMenu title={item.title || '施策'} actions={actions} />
                </div>
              </div>
              <strong className="event-marketing-board-card__title">{item.title || '名称未設定'}</strong>
              <p>{item.message || item.notes || '訴求・メモ未設定'}</p>
              <dl className="event-marketing-board-card__facts">
                <div><dt>{initiativeSchedule(item).label}</dt><dd>{initiativeSchedule(item).value}</dd></div>
                <div><dt>担当</dt><dd>{ownerName}</dd></div>
                <div><dt>予算 実績 / 計画</dt><dd>{formatYen(item.actualBudget)} / {formatYen(item.plannedBudget)}</dd></div>
                <div><dt>記録 / 効果</dt><dd>{formatNumberJP(logCount)}件 / {formatNumberJP(metricCount)}件</dd></div>
              </dl>
              <div className="event-marketing-board-card__latest">
                <span>最新</span>
                <strong>{latestLog?.summary || '実施記録なし'}</strong>
                <small>{latestLog?.occurredAt ? formatDateTimeJST(latestLog.occurredAt) : '—'}</small>
              </div>
            </article>
          ))}
        </div>
      ) : null}
      <DsModal
        open={Boolean(detailInitiative)}
        onClose={() => setDetailInitiative(null)}
        title={detailInitiative?.title || '施策の詳細'}
        ariaLabel="施策の詳細"
        size="md"
        className="event-marketing-board-detail-modal"
        footer={detailInitiative ? [
          view.canEdit ? <DsButton key="log" variant="secondary" size="sm" onClick={() => runDetailAction(() => view.openEditor('log', null, { initiativeId: detailInitiative.id }))}>実施を記録</DsButton> : null,
          view.canEdit ? <DsButton key="metric" variant="create" size="sm" onClick={() => runDetailAction(() => view.openEditor('metric', null, { initiativeId: detailInitiative.id }))}>効果を追加</DsButton> : null,
          detailInitiative.id && view.canViewTasks ? <DsButton key="task" variant="secondary" size="sm" onClick={() => runDetailAction(() => view.reuse.openTask(detailInitiative))}>タスク</DsButton> : null,
          view.canEdit ? <DsButton key="edit" size="sm" onClick={() => runDetailAction(() => view.openEditor('initiative', detailIndex))}>編集</DsButton> : null,
        ] : null}
      >
        {detailInitiative ? (
          <div className="event-marketing-board-detail">
            <div className="event-marketing-board-detail__badges">
              <span className={`event-marketing-status is-${detailInitiative.status}`}>{INITIATIVE_STATUS_LABELS[detailInitiative.status] || detailInitiative.status}</span>
              {detailInitiative.channel ? <span className="event-marketing-board-table__channel">{detailInitiative.channel}</span> : null}
              {summary.reviewInitiativeIds.has(detailId) ? <span className="event-marketing-board-table__review">要記録</span> : null}
            </div>
            <dl className="event-marketing-board-detail__grid">
              <div className="is-wide"><dt>訴求・メモ</dt><dd>{detailInitiative.message || detailInitiative.notes || '未設定'}</dd></div>
              <div><dt>{initiativeSchedule(detailInitiative).label}</dt><dd>{initiativeSchedule(detailInitiative).value}</dd></div>
              <div><dt>担当</dt><dd>{detailOwnerName}</dd></div>
              <div><dt>予算（実績 / 計画）</dt><dd>{formatYen(detailInitiative.actualBudget)} / {formatYen(detailInitiative.plannedBudget)}</dd></div>
              <div><dt>記録 / 効果</dt><dd>{formatNumberJP(detailLogCount)}件 / {formatNumberJP(detailMetricCount)}件</dd></div>
              <div className="is-wide"><dt>最新の実施</dt><dd>{detailLatestLog?.summary || '実施記録なし'}{detailLatestLog?.occurredAt ? <small>{formatDateTimeJST(detailLatestLog.occurredAt)}</small> : null}</dd></div>
            </dl>
          </div>
        ) : null}
      </DsModal>
    </section>
  );
}
